import { useTranslation } from "react-i18next";
import {
  Box,
  Button,
  Card,
  Checkbox,
  Chip,
  Divider,
  FormControl,
  FormControlLabel,
  InputLabel,
  ListSubheader,
  MenuItem,
  Select,
  Stack,
  Switch,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material";
import { describeError, useToast } from "../components/toast";
import { useCallback, useEffect, useMemo, useState } from "react";
import {
  ALERT_HAS_THRESHOLD,
  ALERT_SUBJECT_OF,
  DEFAULT_ALERT_RULE_SPEC,
  HEALTH_ALERT_TYPES,
  type AlertChannel,
  type AlertRule,
  type AlertRuleScope,
  type EffectiveAlertRule,
  type HealthAlertRecord,
  type HealthAlertType,
} from "@shared/types/foyer";
import {
  foyerApi,
  type CompanySummary,
  type PremiseSummary,
} from "../http/foyer";
import { useAuth } from "../auth/AuthContext";
import { tokens } from "../theme";
import { PageContainer, PageHeader } from "../components/PageHeader";
import HelpBadge from "../components/HelpBadge";
import StaffPushOptIn from "../components/StaffPushOptIn";
import {
  PermissionGate,
  useHomePlace,
  usePermission,
  type PlaceRef,
} from "../auth/access";
import { ItemFormSurface } from "../components/EditDrawer";
import { useFormat } from "../i18n/format";
import type { Translate } from "../i18n/translate";
import { errorText } from "../utils/apiError";

/**
 * Health alerting rules (Phase 5).
 *
 * THE PAGE IS A SCOPE PICKER PLUS ONE CARD PER RULE TYPE, deliberately. Rules resolve
 * most-specific-wins (premise > company > global), so the question an operator
 * actually has is "what fires HERE?" - not "list every rule row in the system".
 * Picking a scope first and then showing every type with an explicit
 * inherited/overridden badge answers that directly; a flat CRUD list of rows
 * would make the reader compute precedence in their head.
 */

const typeLabel = (t: Translate, type: HealthAlertType): string =>
  t(`analytics:healthAlertsPage.types.${type}`);
const typeBlurb = (t: Translate, type: HealthAlertType): string =>
  t(`analytics:healthAlertsPage.blurbs.${type}`);
/** The unit each type's threshold is counted in. */
const typeUnit = (t: Translate, type: HealthAlertType): string =>
  t(`analytics:healthAlertsPage.units.${type}`);
const sourceLabel = (
  t: Translate,
  source: EffectiveAlertRule["source"]
): string => t(`analytics:healthAlertsPage.sources.${source}`);

/** Split a free-text recipients box (newline/comma separated) into emails. */
function parseRecipients(text: string): string[] {
  return text
    .split(/[\n,]/)
    .map((s) => s.trim())
    .filter(Boolean);
}

/** The scope the page is currently editing, encoded as one select value. */
type ScopeValue =
  | { kind: "global" }
  | { kind: "company"; companyId: string }
  | { kind: "premise"; premiseId: string };

function encodeScope(s: ScopeValue): string {
  if (s.kind === "global") return "global";
  if (s.kind === "company") return `company:${s.companyId}`;
  return `premise:${s.premiseId}`;
}

function decodeScope(v: string): ScopeValue {
  if (v.startsWith("company:")) {
    return { kind: "company", companyId: v.slice("company:".length) };
  }
  if (v.startsWith("premise:")) {
    return { kind: "premise", premiseId: v.slice("premise:".length) };
  }
  return { kind: "global" };
}

/** Editable state for one rule card. Strings so partial input doesn't fight. */
interface Draft {
  enabled: boolean;
  threshold: string;
  channels: AlertChannel[];
  recipients: string;
}

function draftFrom(eff: EffectiveAlertRule): Draft {
  return {
    enabled: eff.enabled,
    threshold: String(eff.threshold),
    channels: eff.channels.length > 0 ? eff.channels : ["email"],
    recipients: eff.recipients.join("\n"),
  };
}

/** One line of what the rule does now, for the card. */
function ruleSummary(t: Translate, eff: EffectiveAlertRule): string {
  if (!eff.enabled) return t("analytics:healthAlertsPage.summary.off");
  const parts: string[] = [];
  if (ALERT_HAS_THRESHOLD[eff.type]) {
    parts.push(
      t("analytics:healthAlertsPage.summary.threshold", {
        threshold: eff.threshold,
        unit: typeUnit(t, eff.type),
      })
    );
  }
  const channels = eff.channels.length > 0 ? eff.channels : ["email"];
  parts.push(
    t("analytics:healthAlertsPage.summary.via", {
      channels: channels
        .map((c) =>
          c === "push"
            ? t("analytics:healthAlertsPage.summary.push")
            : t("analytics:healthAlertsPage.summary.email")
        )
        .join(t("analytics:healthAlertsPage.summary.channelJoin")),
    })
  );
  parts.push(
    eff.recipients.length > 0
      ? t("analytics:healthAlertsPage.summary.to", {
          recipients: eff.recipients.join(", "),
        })
      : t("analytics:healthAlertsPage.summary.toSegmentLists")
  );
  return parts.join(t("analytics:healthAlertsPage.summary.join"));
}

/**
 * The rule form. Overriding an inherited rule CREATES a row at this scope, so
 * it opens as a modal; editing a rule already set here opens the drawer.
 */
function RuleEditor({
  eff,
  mode,
  scopeLabel,
  busy,
  onClose,
  onSave,
}: {
  eff: EffectiveAlertRule;
  mode: "create" | "edit";
  scopeLabel: string;
  busy: boolean;
  onClose: () => void;
  onSave: (draft: Draft) => Promise<boolean>;
}) {
  const { t } = useTranslation("analytics");
  const [draft, setDraft] = useState<Draft>(() => draftFrom(eff));
  const patch = (next: Partial<Draft>) => setDraft((d) => ({ ...d, ...next }));
  const toggleChannel = (channel: AlertChannel, on: boolean) =>
    patch({
      channels: on
        ? [...draft.channels.filter((c) => c !== channel), channel]
        : draft.channels.filter((c) => c !== channel),
    });

  return (
    <ItemFormSurface
      mode={mode}
      onClose={onClose}
      busy={busy}
      title={
        mode === "create"
          ? t("healthAlertsPage.editor.overrideTitle", {
              type: typeLabel(t, eff.type),
            })
          : t("healthAlertsPage.editor.editTitle", {
              type: typeLabel(t, eff.type),
            })
      }
      subtitle={t("healthAlertsPage.editor.appliesTo", { scope: scopeLabel })}
      data-testid={`alert-rule-editor-${eff.type}`}
      actions={(dismiss) => (
        <>
          <Button color="inherit" disabled={busy} onClick={() => dismiss()}>
            {t("healthAlertsPage.editor.cancel")}
          </Button>
          <Button
            variant="contained"
            disabled={busy}
            onClick={async () => {
              if (await onSave(draft)) dismiss();
            }}
          >
            {mode === "create"
              ? t("healthAlertsPage.editor.saveOverride")
              : t("healthAlertsPage.editor.save")}
          </Button>
        </>
      )}
    >
      <Stack spacing={2.5} sx={{ pt: 1 }}>
        <Typography variant="body2" color="text.secondary">
          {typeBlurb(t, eff.type)}
        </Typography>
        <FormControlLabel
          control={
            <Switch
              checked={draft.enabled}
              disabled={busy}
              onChange={(e) => patch({ enabled: e.target.checked })}
              inputProps={{
                "aria-label": t("healthAlertsPage.editor.enable", {
                  type: typeLabel(t, eff.type),
                }),
              }}
            />
          }
          label={
            draft.enabled
              ? t("healthAlertsPage.editor.on")
              : t("healthAlertsPage.editor.off")
          }
        />

        {/* A boolean condition gets no threshold box: offering a number whose
            value changes nothing is worse than offering none. */}
        {ALERT_HAS_THRESHOLD[eff.type] && (
          <TextField
            size="small"
            type="number"
            label={t("healthAlertsPage.editor.threshold")}
            value={draft.threshold}
            disabled={busy || !draft.enabled}
            onChange={(e) => patch({ threshold: e.target.value })}
            helperText={typeUnit(t, eff.type)}
            sx={{ width: 160 }}
            inputProps={{ min: 1 }}
          />
        )}

        <Box>
          <Typography variant="caption" color="text.secondary">
            {t("healthAlertsPage.editor.deliverVia")}
          </Typography>
          <Stack direction="row" spacing={0.5}>
            <FormControlLabel
              control={
                <Checkbox
                  size="small"
                  checked={draft.channels.includes("email")}
                  disabled={busy || !draft.enabled}
                  onChange={(e) => toggleChannel("email", e.target.checked)}
                />
              }
              label={t("healthAlertsPage.editor.email")}
            />
            <Tooltip title={t("healthAlertsPage.editor.pushTooltip")}>
              <FormControlLabel
                control={
                  <Checkbox
                    size="small"
                    checked={draft.channels.includes("push")}
                    disabled={busy || !draft.enabled}
                    onChange={(e) => toggleChannel("push", e.target.checked)}
                  />
                }
                label={t("healthAlertsPage.editor.push")}
              />
            </Tooltip>
          </Stack>
        </Box>

        <TextField
          size="small"
          label={t("healthAlertsPage.editor.recipients")}
          value={draft.recipients}
          disabled={busy || !draft.enabled}
          onChange={(e) => patch({ recipients: e.target.value })}
          multiline
          minRows={2}
          placeholder={t("healthAlertsPage.editor.recipientsPlaceholder")}
          helperText={t("healthAlertsPage.editor.recipientsHelp")}
          fullWidth
        />
      </Stack>
    </ItemFormSurface>
  );
}

export default function HealthAlertsPage() {
  const { user } = useAuth();
  // Rules for every company or for any one company are written at Nenda.
  const isAdmin = usePermission("screens/alerts", "edit", "nenda");
  const homeCompanyId = user?.access?.homeCompanyId ?? null;

  const [premises, setPremises] = useState<PremiseSummary[]>([]);
  const [companies, setCompanies] = useState<CompanySummary[]>([]);
  const [rules, setRules] = useState<AlertRule[]>([]);
  const [alerts, setAlerts] = useState<HealthAlertRecord[]>([]);
  const [scope, setScope] = useState<ScopeValue>({ kind: "global" });
  const home = useHomePlace();
  const rulePlace: PlaceRef =
    scope.kind === "global"
      ? "nenda"
      : scope.kind === "company"
        ? { companyId: scope.companyId }
        : { premiseId: scope.premiseId };
  const ruleLevel = scope.kind === "global" ? "administrate" : "edit";
  const [effective, setEffective] = useState<EffectiveAlertRule[] | null>(null);
  /**
   * The rule being edited. `mode` is fixed when the surface opens: saving an
   * override adds the rule row, and a mode read live would flip the closing
   * modal into the drawer mid-animation.
   */
  const [editing, setEditing] = useState<{
    eff: EffectiveAlertRule;
    mode: "create" | "edit";
  } | null>(null);
  const [loading, setLoading] = useState(true);
  const [savingType, setSavingType] = useState<HealthAlertType | null>(null);
  const [error, setError] = useState<string | null>(null);
  const toast = useToast();
  const { t } = useTranslation(["analytics", "common"]);
  const fmt = useFormat();

  // ── Load ─────────────────────────────────────────────────────────────────

  const loadRules = useCallback(async () => {
    const { rules: rs } = await foyerApi.listAlertRules();
    setRules(rs);
    return rs;
  }, []);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      setLoading(true);
      setError(null);
      try {
        const [ps, rs, al] = await Promise.all([
          foyerApi.listPremises(),
          foyerApi.listAlertRules(),
          foyerApi.listAlerts({ limit: 50 }),
        ]);
        if (cancelled) return;
        setPremises(ps);
        setRules(rs.rules);
        setAlerts(al.alerts);
        // Only a user who writes rules at Nenda picks a company; a tenant's own
        // company is their home, so a failure here must not blank the page.
        if (isAdmin) {
          try {
            setCompanies(await foyerApi.listCompanies());
          } catch {
            /* non-fatal - the company tier just won't be selectable */
          }
        }
      } catch (e) {
        if (!cancelled) {
          setError(errorText(e, t) || t("healthAlertsPage.loadFailed"));
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [isAdmin, t]);

  /**
   * The effective rule set for the selected scope. For a premise the server
   * computes it (so precedence lives in exactly one place); for the global and
   * company tiers there is no single premise to resolve against, so the card
   * shows that tier's own rows falling back to the built-in defaults.
   */
  const refreshEffective = useCallback(
    async (s: ScopeValue, ruleSet: AlertRule[]) => {
      if (s.kind === "premise") {
        const res = await foyerApi.effectiveAlertRules(s.premiseId);
        return res.effective;
      }
      return HEALTH_ALERT_TYPES.map((type): EffectiveAlertRule => {
        const own = ruleSet.find(
          (r) =>
            r.type === type &&
            r.scope === s.kind &&
            (s.kind === "global" || r.companyId === s.companyId)
        );
        if (own) {
          return {
            type,
            enabled: own.enabled,
            threshold: own.threshold,
            channels: own.channels,
            recipients: own.recipients,
            source: s.kind,
            ruleId: own.ruleId,
          };
        }
        const fallback = ruleSet.find(
          (r) => r.type === type && r.scope === "global"
        );
        if (s.kind === "company" && fallback) {
          return {
            type,
            enabled: fallback.enabled,
            threshold: fallback.threshold,
            channels: fallback.channels,
            recipients: fallback.recipients,
            source: "global",
            ruleId: fallback.ruleId,
          };
        }
        // Read the built-in defaults from the shared spec rather than
        // restating them: a new rule type that ships ON would otherwise show
        // as OFF here until somebody remembered this branch. `threshold: null`
        // in the spec means "inherit settings.alertOfflineMinutes"; the server
        // substitutes the live value, this preview shows its default.
        const spec = DEFAULT_ALERT_RULE_SPEC[type];
        return {
          type,
          enabled: spec.enabled,
          threshold: spec.threshold ?? 60,
          channels: ["email"],
          recipients: [],
          source: "default",
        };
      });
    },
    []
  );

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const eff = await refreshEffective(scope, rules);
        if (cancelled) return;
        setEffective(eff);
      } catch (e) {
        if (!cancelled) {
          setError(errorText(e, t) || t("healthAlertsPage.resolveFailed"));
        }
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [scope, rules, refreshEffective, t]);

  // ── Mutations ────────────────────────────────────────────────────────────

  /** The rule row for this type AT the selected scope, if one exists. */
  const ownRuleFor = useCallback(
    (type: HealthAlertType): AlertRule | undefined =>
      rules.find(
        (r) =>
          r.type === type &&
          r.scope === scope.kind &&
          (scope.kind === "global" ||
            (scope.kind === "company"
              ? r.companyId === scope.companyId
              : r.premiseId === scope.premiseId))
      ),
    [rules, scope]
  );

  /** Resolves true on success, so the editor can slide out before closing. */
  async function save(type: HealthAlertType, draft: Draft): Promise<boolean> {
    setSavingType(type);
    try {
      await foyerApi.saveAlertRule({
        type,
        scope: scope.kind as AlertRuleScope,
        ...(scope.kind === "company" ? { companyId: scope.companyId } : {}),
        ...(scope.kind === "premise" ? { premiseId: scope.premiseId } : {}),
        enabled: draft.enabled,
        threshold: Number(draft.threshold) || null,
        channels: draft.channels,
        recipients: parseRecipients(draft.recipients),
      });
      await loadRules();
      toast.success(t("healthAlertsPage.saved", { type: typeLabel(t, type) }));
      return true;
    } catch (e) {
      toast.error(
        describeError(t, "saveAlertRule", e, { type: typeLabel(t, type) })
      );
      return false;
    } finally {
      setSavingType(null);
    }
  }

  async function resetToInherited(type: HealthAlertType) {
    const own = ownRuleFor(type);
    if (!own) return;
    setSavingType(type);
    try {
      await foyerApi.deleteAlertRule(own.ruleId);
      await loadRules();
      toast.success(
        t("healthAlertsPage.inheritsAgain", { type: typeLabel(t, type) })
      );
    } catch (e) {
      toast.error(
        describeError(t, "resetAlertRule", e, { type: typeLabel(t, type) })
      );
    } finally {
      setSavingType(null);
    }
  }

  // ── Derived ──────────────────────────────────────────────────────────────

  const premiseOptions = useMemo(
    () =>
      [...premises].sort((a, b) => a.premiseName.localeCompare(b.premiseName)),
    [premises]
  );

  const openAlerts = useMemo(
    () => alerts.filter((a) => a.status === "open"),
    [alerts]
  );

  const premiseName = useCallback(
    (slug: string) =>
      premises.find((p) => p.premiseId === slug)?.premiseName ?? slug,
    [premises]
  );

  const scopeLabel =
    scope.kind === "global"
      ? t("healthAlertsPage.scope.allCompanies")
      : scope.kind === "company"
        ? (companies.find((c) => c.id === scope.companyId)?.name ??
          t("healthAlertsPage.scope.thisCompany"))
        : premiseName(scope.premiseId);

  return (
    <PageContainer>
      <PageHeader
        eyebrow={t("healthAlertsPage.eyebrow")}
        title={t("healthAlertsPage.title")}
        subtitle={t("healthAlertsPage.subtitle", { scope: scopeLabel })}
        help={t("healthAlertsPage.help")}
      />

      {error && (
        <Typography color="error" variant="body2">
          {error}
        </Typography>
      )}

      {/* ── Scope picker ───────────────────────────────────────────────── */}
      <Card variant="outlined" sx={{ p: 2.5, position: "relative" }}>
        <HelpBadge title={t("healthAlertsPage.scopeHelp")} />
        <FormControl size="small" sx={{ minWidth: 320, maxWidth: "100%" }}>
          <InputLabel id="alert-scope-label">
            {t("healthAlertsPage.configureFor")}
          </InputLabel>
          <Select
            labelId="alert-scope-label"
            label={t("healthAlertsPage.configureFor")}
            value={encodeScope(scope)}
            onChange={(e) => setScope(decodeScope(e.target.value))}
            data-testid="alert-scope-select"
          >
            {isAdmin && (
              <MenuItem value="global">
                {t("healthAlertsPage.allCompaniesDefault")}
              </MenuItem>
            )}
            {isAdmin && companies.length > 0 && (
              <ListSubheader>{t("healthAlertsPage.companies")}</ListSubheader>
            )}
            {isAdmin &&
              companies.map((c) => (
                <MenuItem key={c.id} value={`company:${c.id}`}>
                  {c.name}
                </MenuItem>
              ))}
            {!isAdmin && homeCompanyId && (
              <MenuItem value={`company:${homeCompanyId}`}>
                {t("healthAlertsPage.myCompanyDefault")}
              </MenuItem>
            )}
            <ListSubheader>{t("healthAlertsPage.premises")}</ListSubheader>
            {premiseOptions.map((p) => (
              <MenuItem key={p.premiseId} value={`premise:${p.premiseId}`}>
                {p.premiseName}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
      </Card>

      {/* ── Push opt-in (per browser, gesture-gated) ───────────────────── */}
      <PermissionGate resource="screens/alerts" level="edit" place={home}>
        <StaffPushOptIn />
      </PermissionGate>

      {/* ── Rule cards ─────────────────────────────────────────────────── */}
      {loading || !effective ? (
        <Stack spacing={1.5}>
          {HEALTH_ALERT_TYPES.map((_, i) => (
            <Box
              key={i}
              sx={{
                height: 120,
                borderRadius: 2,
                bgcolor: tokens.rowAlt,
                opacity: 0.5,
              }}
            />
          ))}
        </Stack>
      ) : (
        <Stack spacing={2}>
          {effective.map((eff) => {
            const own = ownRuleFor(eff.type);
            const inherited = !own;
            const busy = savingType === eff.type;
            return (
              <Card
                key={eff.type}
                variant="outlined"
                sx={{ p: 2.5 }}
                data-testid={`alert-rule-${eff.type}`}
              >
                <Stack spacing={2}>
                  <Stack
                    direction={{ xs: "column", sm: "row" }}
                    spacing={1.5}
                    sx={{
                      justifyContent: "space-between",
                      alignItems: { sm: "center" },
                    }}
                  >
                    <Box sx={{ minWidth: 0 }}>
                      <Stack direction="row" spacing={1} alignItems="center">
                        <Typography sx={{ fontWeight: "fontWeightSemibold" }}>
                          {typeLabel(t, eff.type)}
                        </Typography>
                        <Chip
                          size="small"
                          variant={inherited ? "outlined" : "filled"}
                          color={inherited ? "default" : "primary"}
                          label={
                            inherited
                              ? t("healthAlertsPage.inherited", {
                                  source: sourceLabel(t, eff.source),
                                })
                              : t("healthAlertsPage.overriddenHere")
                          }
                        />
                        <Chip
                          size="small"
                          variant="outlined"
                          label={
                            ALERT_SUBJECT_OF[eff.type] === "premise"
                              ? t("healthAlertsPage.perSite")
                              : t("healthAlertsPage.perDevice")
                          }
                        />
                      </Stack>
                      <Typography
                        variant="body2"
                        color="text.secondary"
                        sx={{ mt: 0.5 }}
                      >
                        {typeBlurb(t, eff.type)}
                      </Typography>
                    </Box>
                    <Chip
                      size="small"
                      color={eff.enabled ? "success" : "default"}
                      variant={eff.enabled ? "filled" : "outlined"}
                      label={
                        eff.enabled
                          ? t("healthAlertsPage.on")
                          : t("healthAlertsPage.off")
                      }
                      data-testid={`alert-rule-${eff.type}-status`}
                    />
                  </Stack>

                  <Divider />

                  <Typography variant="body2" color="text.secondary">
                    {ruleSummary(t, eff)}
                  </Typography>

                  <Stack direction="row" spacing={1.5}>
                    <PermissionGate
                      resource="screens/alerts"
                      level={ruleLevel}
                      place={rulePlace}
                      deniedProps={{ disabled: true }}
                    >
                      <Button
                        variant="contained"
                        size="small"
                        disabled={busy}
                        onClick={() =>
                          setEditing({
                            eff,
                            mode: inherited ? "create" : "edit",
                          })
                        }
                      >
                        {inherited
                          ? t("healthAlertsPage.overrideHere")
                          : t("healthAlertsPage.edit")}
                      </Button>
                    </PermissionGate>
                    {!inherited && (
                      <PermissionGate
                        resource="screens/alerts"
                        level={ruleLevel}
                        place={rulePlace}
                        deniedProps={{ disabled: true }}
                      >
                        <Button
                          size="small"
                          color="inherit"
                          disabled={busy}
                          onClick={() => void resetToInherited(eff.type)}
                        >
                          {t("healthAlertsPage.resetToInherited")}
                        </Button>
                      </PermissionGate>
                    )}
                  </Stack>
                </Stack>
              </Card>
            );
          })}
        </Stack>
      )}

      {editing && (
        <RuleEditor
          eff={editing.eff}
          mode={editing.mode}
          scopeLabel={scopeLabel}
          busy={savingType === editing.eff.type}
          onClose={() => setEditing(null)}
          onSave={(draft) => save(editing.eff.type, draft)}
        />
      )}

      {/* ── Active alerts ──────────────────────────────────────────────── */}
      <Card variant="outlined" sx={{ p: 2.5, position: "relative" }}>
        <HelpBadge title={t("healthAlertsPage.activeAlertsHelp")} />
        <Typography sx={{ fontWeight: "fontWeightSemibold", mb: 1.5 }}>
          {openAlerts.length > 0
            ? t("healthAlertsPage.activeAlertsCount", {
                count: openAlerts.length,
              })
            : t("healthAlertsPage.activeAlerts")}
        </Typography>
        {loading ? (
          <Typography variant="body2" color="text.secondary">
            {t("healthAlertsPage.loading")}
          </Typography>
        ) : openAlerts.length === 0 ? (
          <Typography variant="body2" color="text.secondary">
            {t("healthAlertsPage.nothingAlerting")}
          </Typography>
        ) : (
          <Stack divider={<Divider />} spacing={0}>
            {openAlerts.map((a) => (
              <Stack
                key={a.alertId}
                direction="row"
                spacing={1.5}
                sx={{ py: 1, alignItems: "center", flexWrap: "wrap" }}
              >
                <Chip
                  size="small"
                  color="error"
                  variant="outlined"
                  label={typeLabel(t, a.type)}
                />
                <Typography
                  variant="body2"
                  sx={{ fontWeight: "fontWeightMedium" }}
                >
                  {a.roomId ??
                    a.model ??
                    a.screenId ??
                    premiseName(a.premiseId)}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  {premiseName(a.premiseId)}
                </Typography>
                {a.observed !== undefined && (
                  <Typography variant="body2" color="text.secondary">
                    {t("healthAlertsPage.observed", {
                      observed: a.observed,
                      unit: typeUnit(t, a.type),
                    })}
                  </Typography>
                )}
                <Box sx={{ flex: 1 }} />
                <Typography variant="caption" color="text.secondary">
                  {t("healthAlertsPage.since", {
                    at: fmt.dateTime(a.openedAt),
                  })}
                </Typography>
                {!a.notifiedOpenAt && (
                  <Tooltip title={t("healthAlertsPage.notNotifiedTooltip")}>
                    <Chip
                      size="small"
                      variant="outlined"
                      label={t("healthAlertsPage.notNotified")}
                    />
                  </Tooltip>
                )}
              </Stack>
            ))}
          </Stack>
        )}
      </Card>
    </PageContainer>
  );
}
